import jsPDF from 'jspdf'
import 'jspdf-autotable'
import JsBarcode from 'jsbarcode'
import moment from 'moment'
import QRCode from 'qrcode'
import { wtFormatRoundedValue } from 'src/helpers/commons/utils'
import {
  SOURCE_TYPE_DEFAULT,
  SOURCE_TYPE_JV_PAYMENT,
  SOURCE_TYPE_SALARY_PAYMENT,
  STATUS_CANCELLED,
  VOUCHER_TYPE_BANK_PAYMENT_VOUCHER,
  VOUCHER_TYPE_BANK_RECEIPT_VOUCHER,
  VOUCHER_TYPE_CASH_PAYMENT_VOUCHER,
  VOUCHER_TYPE_CASH_RECEIPT_VOUCHER,
  VOUCHER_TYPE_JOURNAL_VOUCHER,
  VOUCHER_TYPE_NAMES,
} from 'src/models/vouchers'
import { wtGetFromLocalStorage } from 'src/helpers/commons/LocalStorage'
import { wtGetDateOnly, wtGetDateTimeFormat } from 'src/helpers/commons/moment'
import { STATE_PROVINCE_NAMES } from 'src/models/sale-orders'

const user = wtGetFromLocalStorage('authUser')
const VoucherPDFBatch = async (vouchers, appendPages) => {
  const doc = new jsPDF()

  for (const [index, pdfData] of vouchers.entries()) {
    let yPosition = pdfData?.pdf_meta?.headerLayout === 'jv_payment' ? 82 : 69.5
    const voucherTitle = pdfData?.pdf_meta?.title || VOUCHER_TYPE_NAMES[pdfData.voucher.type]
    const voucherTypeLabel = pdfData?.pdf_meta?.typeLabel || VOUCHER_TYPE_NAMES[pdfData.voucher.type]
    const referenceLabel = pdfData?.pdf_meta?.referenceLabel || 'Pay to:'
    const voucherDetails = Array.isArray(pdfData?.voucher_detail) ? pdfData.voucher_detail : []
    const getEmployeeDisplay = (employee) => {
      if (!employee) {
        return ''
      }

      const employeeName =
        employee.full_name ||
        employee.name ||
        `${employee.first_name || ''} ${employee.last_name || ''}`.trim()

      if (!employeeName) {
        return ''
      }

      return employee.id ? `${employee.id}- ${employeeName}` : employeeName
    }
    const firstProject =
      voucherDetails.find((item) => item?.project?.project_name || item?.project?.name)?.project || null
    const projectDisplayName = firstProject?.project_name || firstProject?.name || ''
    const uniqueEmployees = [
      ...new Set(
        voucherDetails.map((item) => getEmployeeDisplay(item?.employee)).filter(Boolean),
      ),
    ]
    const uniqueEmployeeKeys = [
      ...new Set(
        voucherDetails
          .map((item) => item?.employee)
          .filter(Boolean)
          .map((employee) => employee.id || getEmployeeDisplay(employee))
          .filter(Boolean),
      ),
    ]
    const employeeDisplayName = uniqueEmployees.join(', ')
    const uniquePartyNames = [
      ...new Set(voucherDetails.map((item) => item?.party?.party_name || item?.party?.name).filter(Boolean)),
    ]
    const partyDisplayName = uniquePartyNames.join(', ')
    const getPartyDisplay = (party) => party?.party_name || party?.name || ''
    const uniquePartyNtNs = [
      ...new Set(voucherDetails.map((item) => item?.party?.ntn).filter(Boolean)),
    ]
    const uniquePartyKeys = [
      ...new Set(
        voucherDetails
          .map((item) => item?.party)
          .filter(Boolean)
          .map((party) => party.id || party.party_name || party.name)
          .filter(Boolean),
      ),
    ]
    const singleEmployeeDisplayName = uniqueEmployees.length === 1 ? uniqueEmployees[0] : ''
    const singlePartyDisplayName = uniquePartyNames.length === 1 ? uniquePartyNames[0] : ''
    const singlePartyNtnDisplay = uniquePartyNames.length === 1 && uniquePartyNtNs.length === 1 ? uniquePartyNtNs[0] : ''
    const singleEmployeeRecord =
      uniqueEmployeeKeys.length === 1 ? voucherDetails.find((item) => item?.employee)?.employee || null : null
    const singlePartyRecord =
      uniquePartyKeys.length === 1 ? voucherDetails.find((item) => item?.party)?.party || null : null
    const isDirectBankVoucher =
      pdfData?.voucher?.source_type === SOURCE_TYPE_DEFAULT &&
      [
        VOUCHER_TYPE_BANK_PAYMENT_VOUCHER,
        VOUCHER_TYPE_BANK_RECEIPT_VOUCHER,
      ].includes(pdfData?.voucher?.type)
    const singlePartyDisplayWithCode = getPartyDisplay(singlePartyRecord) || singlePartyDisplayName
    const singlePartyTaxDisplay = [singlePartyRecord?.ntn, singlePartyRecord?.cnic].filter(Boolean).join(' / ')
    const singlePartyFilerDisplay =
      singlePartyRecord?.filer === 1 ? 'Yes' : singlePartyRecord?.filer === 2 ? 'No' : ''
    const singlePartyStateDisplay =
      STATE_PROVINCE_NAMES[singlePartyRecord?.state] || `${singlePartyRecord?.state || ''}`.trim()
    const singleEmployeeCnicDisplay = singleEmployeeRecord?.cnic || ''
    const singleEmployeeDesignationDisplay = singleEmployeeRecord?.designation?.name || ''
    const showSingleSelectionSummaryOnly = pdfData?.pdf_meta?.showSingleSelectionSummaryOnly === true
    const shouldAutoShowEmployeeBankDetails =
      isDirectBankVoucher && pdfData?.voucher?.is_employee_show == 1 && uniqueEmployeeKeys.length === 1
    const shouldAutoShowPartyBankDetails =
      isDirectBankVoucher && pdfData?.voucher?.is_party_show == 1 && uniquePartyKeys.length === 1
    const renderSelectionDetailsBesideBank =
      (pdfData?.pdf_meta?.sideBySideBankSelectionDetails === true &&
        pdfData?.pdf_meta?.headerLayout === 'jv_payment' &&
        [
          VOUCHER_TYPE_BANK_PAYMENT_VOUCHER,
          VOUCHER_TYPE_BANK_RECEIPT_VOUCHER,
        ].includes(pdfData?.voucher?.type)) ||
      shouldAutoShowEmployeeBankDetails ||
      shouldAutoShowPartyBankDetails
    const showPartyDetailsBlock =
      pdfData?.pdf_meta?.headerLayout === 'jv_payment' && !!singlePartyRecord && !renderSelectionDetailsBesideBank
    const showEmployeeDetailsBlock =
      pdfData?.pdf_meta?.headerLayout === 'jv_payment' && !!singleEmployeeRecord && !renderSelectionDetailsBesideBank
    const showEmployeeColumn =
      typeof pdfData?.pdf_meta?.showEmployeeColumn === 'boolean'
        ? pdfData.pdf_meta.showEmployeeColumn
        : false
    const showPartyColumn =
      typeof pdfData?.pdf_meta?.showPartyColumn === 'boolean'
        ? pdfData.pdf_meta.showPartyColumn
        : false
    const logoPath = pdfData?.voucher?.company?.logo || ''
    const company = pdfData?.voucher?.company || null
    const logo = process.env.PUBLIC_URL + '/logos' + `/${logoPath}`

    const pageWidth = doc.internal.pageSize.width
    doc.setTextColor('#3c4b64')
    doc.setFont('Helvetica', 'bold')
    doc.setTextColor('#3c4b64')
    doc.setFont('Helvetica', 'bold') //Header middle branch logo

    if (company?.logo_width && company?.logo_height && logoPath) {
      doc.addImage(logo, 'PNG', pageWidth - 200, 4, company.logo_width, company.logo_height)
    }
    // doc.line(pageWidth - 173, 7, pageWidth - 173, 27) // Adjust the coordinates as needed

    doc.setFontSize(14)
    doc.text(voucherTitle, pageWidth - 12, 10, { align: 'right' })


    doc.setFontSize(12)
    doc.setTextColor('#000000')
    doc.setFont('Helvetica', 'bold')

    doc.text('Voucher #', pageWidth - 90, 35, { align: 'left' })
    doc.setFont('Helvetica', 'bold')
    doc.setTextColor('#0202f7')
    doc.text(`${pdfData.voucher.voucher_no ? `${pdfData.voucher.type_short_name}-${pdfData.voucher.company_voucher_no}-${pdfData.voucher.voucher_no}` : ''}`, pageWidth - 67, 35, {
      align: 'left',
    })
    doc.setTextColor('#000000')
    doc.text('Monthly Voucher No #', pageWidth - 90, 40, { align: 'left' })
    doc.setFont('Helvetica', 'bold')
    doc.setTextColor('#0202f7')
    doc.text(
      `${pdfData.voucher.monthly_voucher_no ? `${pdfData.voucher.monthly_voucher_no}` : ''}`,
      pageWidth - 44,
      40,
      {
        align: 'left',
      },
    )

    if (pdfData.voucher.status === STATUS_CANCELLED) {
      const watermarkFontSize = 100
      doc.setTextColor(255, 145, 145) // Set the color of the watermark text
      doc.setFont('Helvetica', 'bold') //Header middle branch logo
      doc.setFontSize(watermarkFontSize)
      doc.text(140, 200, 'CANCELLED', null, 45, 'center')
    }

    //////left side///////
    doc.setFontSize(10)
    doc.setTextColor('#000000')
    doc.setFont('Helvetica', 'bold')

    if (pdfData?.pdf_meta?.headerLayout === 'jv_payment') {
      const leftLabelX = pageWidth - 195
      const leftValueX = pageWidth - 170
      const rightLabelX = pageWidth - 90
      const rightValueX = pageWidth - 62
      const leftSummaryRows = [
        { label: 'Type:', value: pdfData.voucher.type ? voucherTypeLabel : '' },
        { label: 'Project:', value: projectDisplayName },
        { label: 'Voucher Date:', value: pdfData.voucher.voucher_date ? pdfData.voucher.voucher_date : '' },
        { label: 'Cheque No:', value: pdfData.voucher.cheque_no ? pdfData.voucher.cheque_no : '' },
      ]
      const rightSummaryRows = [
        { label: 'Branch:', value: pdfData.voucher.branch ? pdfData.voucher.branch.name : '' },
        { label: 'Creation Date:', value: pdfData.voucher.created_at ? wtGetDateTimeFormat(pdfData.voucher.created_at) : '' },
        { label: referenceLabel, value: pdfData.voucher.reference ? pdfData.voucher.reference : '' },
      ]

      if (pdfData?.pdf_meta?.showSelectionSummary === true) {
        if (!showSingleSelectionSummaryOnly && employeeDisplayName) {
          rightSummaryRows.push({ label: 'Employee Name:', value: employeeDisplayName })
        } else if (
          showSingleSelectionSummaryOnly &&
          singleEmployeeDisplayName &&
          !showEmployeeDetailsBlock &&
          !renderSelectionDetailsBesideBank
        ) {
          rightSummaryRows.push({ label: 'Employee Name:', value: singleEmployeeDisplayName })
        }

        if (!showSingleSelectionSummaryOnly && partyDisplayName) {
          leftSummaryRows.push({ label: 'Party Name:', value: partyDisplayName })
        } else if (
          showSingleSelectionSummaryOnly &&
          singlePartyDisplayName &&
          !showPartyDetailsBlock &&
          !renderSelectionDetailsBesideBank
        ) {
          leftSummaryRows.push({ label: 'Party Name:', value: singlePartyDisplayWithCode })
        }

        if (showSingleSelectionSummaryOnly && singlePartyNtnDisplay && !renderSelectionDetailsBesideBank) {
          rightSummaryRows.push({ label: 'Party NTN:', value: singlePartyNtnDisplay })
        }
      }

      const visibleLeftSummaryRows = leftSummaryRows.filter((row) => row.value)
      const visibleRightSummaryRows = rightSummaryRows.filter((row) => row.value)

      visibleLeftSummaryRows.forEach((row, index) => {
        const y = 45 + index * 5
        doc.setTextColor('#000000')
        doc.setFont('Helvetica', 'bold')
        doc.text(row.label, leftLabelX, y, { align: 'left' })
        doc.setFont('Helvetica', 'normal')
        doc.setTextColor('#0202f7')
        doc.text(`${row.value}`, leftValueX, y, { align: 'left' })
      })

      visibleRightSummaryRows.forEach((row, index) => {
        const y = 45 + index * 5
        doc.setTextColor('#000000')
        doc.setFont('Helvetica', 'bold')
        doc.text(row.label, rightLabelX, y, { align: 'left' })
        doc.setFont('Helvetica', 'normal')
        doc.setTextColor('#0202f7')
        doc.text(`${row.value}`, rightValueX, y, { align: 'left' })
      })

      let detailsBottomY = 0

      if (showPartyDetailsBlock) {
        const headingY = 72
        const rowStartY = headingY + 9
        const detailRowGap = 5
        const detailValueMaxWidth = pageWidth - leftValueX - 10
        const partyDetailsRows = [
          { label: 'Party Name:', value: singlePartyDisplayWithCode || '-' },
          { label: 'NTN/CNIC:', value: singlePartyTaxDisplay || '-' },
          { label: 'Filer:', value: singlePartyFilerDisplay || '-' },
          { label: 'State:', value: singlePartyStateDisplay || '-' },
        ]

        doc.setFontSize(13)
        doc.setTextColor('#0202f7')
        doc.setFont('Helvetica', 'bold')
        doc.text('Party Details:', leftLabelX, headingY)
        doc.setLineWidth(0.2)
        doc.line(leftLabelX, headingY + 1, leftLabelX + 30, headingY + 1)

        doc.setFontSize(10)
        let currentRowY = rowStartY
        partyDetailsRows.forEach((row) => {
          const valueLines = doc.splitTextToSize(`${row.value}`, detailValueMaxWidth)
          doc.setTextColor('#000000')
          doc.setFont('Helvetica', 'bold')
          doc.text(row.label, leftLabelX, currentRowY, { align: 'left' })
          doc.setFont('Helvetica', 'normal')
          doc.setTextColor('#0202f7')
          doc.text(valueLines, leftValueX, currentRowY, { align: 'left' })
          currentRowY += Math.max(valueLines.length, 1) * detailRowGap
        })

        detailsBottomY = Math.max(detailsBottomY, currentRowY - detailRowGap + 4)
      }

      if (showEmployeeDetailsBlock) {
        const headingY = 69
        const rowStartY = headingY + 9
        const detailLabelX = showPartyDetailsBlock ? rightLabelX : leftLabelX
        const employeeDetailsRows = [
          { label: 'Employee Name:', value: singleEmployeeDisplayName || '-' },
          { label: 'CNIC:', value: singleEmployeeCnicDisplay || '-' },
          { label: 'Designation:', value: singleEmployeeDesignationDisplay || '-' },
        ]
        const previousFontSize = doc.getFontSize()
        const previousFont = doc.getFont()
        doc.setFont('Helvetica', 'bold')
        doc.setFontSize(10)
        const employeeLabelOffset =
          Math.ceil(Math.max(...employeeDetailsRows.map((row) => doc.getTextWidth(`${row.label}`)), 0)) + 3
        doc.setFont(previousFont.fontName, previousFont.fontStyle)
        doc.setFontSize(previousFontSize)
        const detailValueX = detailLabelX + employeeLabelOffset

        doc.setFontSize(13)
        doc.setTextColor('#0202f7')
        doc.setFont('Helvetica', 'bold')
        doc.text('Employee Details:', detailLabelX, headingY)
        doc.setLineWidth(0.2)
        doc.line(detailLabelX, headingY + 1, detailLabelX + 36, headingY + 1)

        doc.setFontSize(10)
        employeeDetailsRows.forEach((row, index) => {
          const y = rowStartY + index * 5
          doc.setTextColor('#000000')
          doc.setFont('Helvetica', 'bold')
          doc.text(row.label, detailLabelX, y, { align: 'left' })
          doc.setFont('Helvetica', 'normal')
          doc.setTextColor('#0202f7')
          doc.text(`${row.value}`, detailValueX, y, { align: 'left' })
        })

        detailsBottomY = Math.max(detailsBottomY, rowStartY + (employeeDetailsRows.length - 1) * 5 + 4)
      }

      const headerRowCount = Math.max(visibleLeftSummaryRows.length, visibleRightSummaryRows.length, 1)
      const lastHeaderRowY = 45 + (headerRowCount - 1) * 5
      yPosition = Math.max(lastHeaderRowY + 10, 70, detailsBottomY)
    } else {
      doc.text('Type:', pageWidth - 195, 45, { align: 'left' })
      doc.setFont('Helvetica', 'normal')
      doc.setTextColor('#0202f7')
      doc.text(
        `${pdfData.voucher.type ? voucherTypeLabel : ''}`,
        pageWidth - 183,
        45,
        {
          align: 'left',
        },
      )

      doc.setTextColor('#000000')
      doc.setFont('Helvetica', 'bold')
        doc.text('Voucher Date:', pageWidth - 195, 50)
      doc.setFont('Helvetica', 'normal')
      doc.setTextColor('#0202f7')
      doc.text(`${pdfData.voucher.voucher_date ? pdfData.voucher.voucher_date : ''}`, pageWidth - 165, 50)

      if (pdfData.voucher.cheque_no) {
        doc.setTextColor('#000000')
        doc.setFont('Helvetica', 'bold')
        doc.text('Cheque No:', pageWidth - 195, 55)
        doc.setFont('Helvetica', 'normal')
        doc.setTextColor('#0202f7')
        doc.text(
          `${pdfData.voucher.cheque_no ? pdfData.voucher.cheque_no : ''}`,
          pageWidth - 175,
          55,
        )
      }

      if (pdfData.voucher.reference) {
        doc.setTextColor('#000000')
        doc.setFont('Helvetica', 'bold')
        doc.text(referenceLabel, pageWidth - 195, 60)
        doc.setFont('Helvetica', 'normal')
        doc.setTextColor('#0202f7')
        doc.text(
          `${pdfData.voucher.reference ? pdfData.voucher.reference : ''}`,
          pageWidth - 182,
          60,
        )
      }

      if (projectDisplayName) {
        doc.setTextColor('#000000')
        doc.setFont('Helvetica', 'bold')
        doc.text('Project:', pageWidth - 195, 65)
        doc.setFont('Helvetica', 'normal')
        doc.setTextColor('#0202f7')
        doc.text(projectDisplayName, pageWidth - 178, 65)
      }

      /////right side//////
      doc.setFontSize(10)
      doc.setTextColor('#000000')
      doc.setFont('Helvetica', 'bold')
        doc.text('Branch:', pageWidth - 90, 45)
      doc.setFont('Helvetica', 'normal')
      doc.setTextColor('#0202f7')
      doc.text(`${pdfData.voucher.branch ? pdfData.voucher.branch.name : ''}`, pageWidth - 76, 45)

      doc.setTextColor('#000000')
      doc.setFont('Helvetica', 'bold')
      doc.text('Creation Date:', pageWidth - 90, 50)
      doc.setFont('Helvetica', 'normal')
      doc.setTextColor('#0202f7')
      doc.text(`${pdfData.voucher.created_at ? wtGetDateTimeFormat(pdfData.voucher.created_at) : ''}`, pageWidth - 62, 50)

      if (pdfData.voucher.cheque_date) {
        doc.setTextColor('#000000')
        doc.setFont('Helvetica', 'bold')
        doc.text('Cheque Date:', pageWidth - 90, 55)
        doc.setFont('Helvetica', 'normal')
        doc.setTextColor('#0202f7')
        doc.text(`${pdfData.voucher.cheque_date ? wtGetDateOnly(pdfData.voucher.cheque_date) : ''}`, pageWidth - 62, 55)
      }
      if (pdfData?.pdf_meta?.showSelectionSummary === true && partyDisplayName) {
        doc.setTextColor('#000000')
        doc.setFont('Helvetica', 'bold')
        doc.text('Party Name:', pageWidth - 90, 60)
        doc.setFont('Helvetica', 'normal')
        doc.setTextColor('#0202f7')
        doc.text(partyDisplayName, pageWidth - 62, 60)
      }

      if (pdfData?.pdf_meta?.showSelectionSummary === true && employeeDisplayName) {
        doc.setTextColor('#000000')
        doc.setFont('Helvetica', 'bold')
        doc.text('Employee:', pageWidth - 90, 65)
        doc.setFont('Helvetica', 'normal')
        doc.setTextColor('#0202f7')
        doc.text(employeeDisplayName, pageWidth - 70, 65)
      }
    }


    ////////////////////// Bank Account Details ////////////////////////
    let bankSectionBottomY = 0
    let bankSpacerRendered = false
    if (
      pdfData.voucher.type === VOUCHER_TYPE_BANK_PAYMENT_VOUCHER ||
      pdfData.voucher.type === VOUCHER_TYPE_BANK_RECEIPT_VOUCHER
    ) {
      if ([SOURCE_TYPE_DEFAULT, SOURCE_TYPE_JV_PAYMENT].includes(pdfData.voucher.source_type)) {
        const bankHeadingY = 71
        const bankRowStartY = bankHeadingY + 9
        const bankLabelX = pageWidth - 195
        const bankValueX = pageWidth - 173
        const bankDetailsRows = [
          {
            label: 'Bank:',
            value: pdfData.voucher.entry_types.bank_name || '',
          },
          {
            label: 'Branch:',
            value: pdfData.voucher.entry_types.bank_branch_name
              ? pdfData.voucher.entry_types.bank_branch_name
              : '',
          },
          {
            label: 'Bank A/C:',
            value: pdfData.voucher.entry_types.account_no ? pdfData.voucher.entry_types.account_no : '',
          },
        ]

        doc.setFontSize(13)
        doc.setTextColor('#0202f7')
        doc.setFont('Helvetica', 'bold')
        doc.text('Bank Account Details:', bankLabelX, bankHeadingY)
        const textWidth = 48
        doc.setLineWidth(0.2)
        doc.line(bankLabelX, bankHeadingY + 1, bankLabelX + textWidth, bankHeadingY + 1)

        doc.setFontSize(10)
        bankDetailsRows.forEach((row, index) => {
          const y = bankRowStartY + index * 5
          doc.setTextColor('#000000')
          doc.setFont('Helvetica', 'bold')
          doc.text(row.label, bankLabelX, y, { align: 'left' })
          doc.setFont('Helvetica', 'normal')
          doc.text(`${row.value}`, bankValueX, y, { align: 'left' })
        })

        bankSectionBottomY = Math.max(
          bankSectionBottomY,
          bankRowStartY + (bankDetailsRows.length - 1) * 5 + 4,
        )

        if (renderSelectionDetailsBesideBank) {
          const detailLabelX = pageWidth - 92
          const detailValueX = pageWidth - 66
          const detailValueMaxWidth = pageWidth - detailValueX - 10
          const detailRowGap = 5
          let detailHeadingY = bankHeadingY

          if (singlePartyRecord) {
            const partyDetailsRows = [
              { label: 'Party Name:', value: singlePartyDisplayWithCode || '-' },
              { label: 'NTN/CNIC:', value: singlePartyTaxDisplay || '-' },
              { label: 'Filer:', value: singlePartyFilerDisplay || '-' },
              { label: 'State:', value: singlePartyStateDisplay || '-' },
            ]

            doc.setFontSize(13)
            doc.setTextColor('#0202f7')
            doc.setFont('Helvetica', 'bold')
            doc.text('Party Details:', detailLabelX, detailHeadingY)
            doc.setLineWidth(0.2)
            doc.line(detailLabelX, detailHeadingY + 1, detailLabelX + 30, detailHeadingY + 1)

            doc.setFontSize(10)
            let currentRowY = detailHeadingY + 9
            partyDetailsRows.forEach((row) => {
              const valueLines = doc.splitTextToSize(`${row.value}`, detailValueMaxWidth)
              doc.setTextColor('#000000')
              doc.setFont('Helvetica', 'bold')
              doc.text(row.label, detailLabelX, currentRowY, { align: 'left' })
              doc.setFont('Helvetica', 'normal')
              doc.setTextColor('#0202f7')
              doc.text(valueLines, detailValueX, currentRowY, { align: 'left' })
              currentRowY += Math.max(valueLines.length, 1) * detailRowGap
            })

            bankSectionBottomY = Math.max(
              bankSectionBottomY,
              currentRowY - detailRowGap + 4,
            )
            detailHeadingY = bankSectionBottomY + 5
          }

          if (singleEmployeeRecord) {
            const employeeDetailsRows = [
              { label: 'Employee Name:', value: singleEmployeeDisplayName || '-' },
              { label: 'CNIC:', value: singleEmployeeCnicDisplay || '-' },
              { label: 'Designation:', value: singleEmployeeDesignationDisplay || '-' },
            ]
            const previousFontSize = doc.getFontSize()
            const previousFont = doc.getFont()
            doc.setFont('Helvetica', 'bold')
            doc.setFontSize(10)
            const employeeLabelOffset =
              Math.ceil(Math.max(...employeeDetailsRows.map((row) => doc.getTextWidth(`${row.label}`)), 0)) + 3
            doc.setFont(previousFont.fontName, previousFont.fontStyle)
            doc.setFontSize(previousFontSize)
            const employeeValueX = detailLabelX + employeeLabelOffset

            doc.setFontSize(13)
            doc.setTextColor('#0202f7')
            doc.setFont('Helvetica', 'bold')
            doc.text('Employee Details:', detailLabelX, detailHeadingY)
            doc.setLineWidth(0.2)
            doc.line(detailLabelX, detailHeadingY + 1, detailLabelX + 36, detailHeadingY + 1)

            doc.setFontSize(10)
            employeeDetailsRows.forEach((row, index) => {
              const y = detailHeadingY + 9 + index * 5
              doc.setTextColor('#000000')
              doc.setFont('Helvetica', 'bold')
              doc.text(row.label, detailLabelX, y, { align: 'left' })
              doc.setFont('Helvetica', 'normal')
              doc.setTextColor('#0202f7')
              doc.text(`${row.value}`, employeeValueX, y, { align: 'left' })
            })

            bankSectionBottomY = Math.max(
              bankSectionBottomY,
              detailHeadingY + 9 + (employeeDetailsRows.length - 1) * 5 + 4,
            )
          }
        }

        doc.setFontSize(14)
        yPosition = Math.max(yPosition, bankSectionBottomY + 4)

        bankSpacerRendered = false
      }
    }


    //////////////////////////// Cash Account Details //////////////////////////
    if (
      pdfData.voucher.type === VOUCHER_TYPE_CASH_PAYMENT_VOUCHER ||
      pdfData.voucher.type === VOUCHER_TYPE_CASH_RECEIPT_VOUCHER
    ) {
      if ([SOURCE_TYPE_DEFAULT, SOURCE_TYPE_JV_PAYMENT].includes(pdfData.voucher.source_type)) {

        doc.setFontSize(13)
        doc.setTextColor('#0202f7')
        doc.setFont('Helvetica', 'bold')
        doc.text('Cash Account Details:', pageWidth - 195, 71)
        const textWidth = 48
        doc.setLineWidth(0.2)
        doc.line(pageWidth - 195, 67, pageWidth - 195 + textWidth, 72)

        doc.setFontSize(10)
        doc.setTextColor('#000000')
        doc.setFont('Helvetica', 'bold')

        doc.text('Account Name:', pageWidth - 195, 80, { align: 'left' })
        doc.setFont('Helvetica', 'normal')
        doc.text(pdfData.voucher.entry_types.name, pageWidth - 167, 80, { align: 'left' })
        // doc.setTextColor('#000000')
        // doc.setFont('Helvetica', 'bold')
        // doc.text('Branch Name:', pageWidth - 195, 80)
        // doc.setFont('Helvetica', 'normal')
        // doc.text(
        //   pdfData.voucher.entry_types.bank_branch_name
        //     ? pdfData.voucher.entry_types.bank_branch_name
        //     : '',
        //   pageWidth - 169,
        //   80,
        // )
        // doc.setTextColor('#000000')
        // doc.setFont('Helvetica', 'bold')
        // doc.text('Bank Account No:', pageWidth - 195, 85)
        // doc.setFont('Helvetica', 'normal')
        // doc.text(
        //   pdfData.voucher.entry_types.account_no ? pdfData.voucher.entry_types.account_no : '',
        //   pageWidth - 162,
        //   85,
        // )

        doc.setFontSize(14)

        const Table1 = [
          [
            {
              content: '',
            },
          ],
          [
            {
              content: '',
            },
          ],
        ]

        doc.autoTable({
          startY: yPosition + 5,
          body: Table1, // Body rows for second table
          theme: 'plain',
          tableLineColor: '#000000',
          tableLineWidth: 0.2,
          tableWidth: 'auto', // Set table width automatically
          styles: {
            lineColor: '#ffffff',
            lineWidth: 0.2,
            halign: 'center',
            font: 'helvetica',
            cellPadding: { top: 1, right: 1, bottom: 1, left: 1 },
          },
          didDrawPage: function (data) { },
        })
      }
    }


    const Table2 = [
      (() => {
        const header = [
          {
            content: 'GL Code',
            styles: { fontStyle: 'bold', halign: 'center' },
          },
          { content: 'Account', styles: { fontStyle: 'bold', halign: 'left' } },
        ]

        if (showEmployeeColumn) {
          header.push({ content: 'Employee', styles: { fontStyle: 'bold', halign: 'left' } })
        }

        if (showPartyColumn) {
          header.push({ content: 'Party', styles: { fontStyle: 'bold', halign: 'left' } })
        }

        header.push(
          { content: 'Debit', styles: { fontStyle: 'bold', halign: 'center' } },
          {
            content: 'Credit',
            styles: { fontStyle: 'bold', halign: 'center' },
          },
          {
            content: 'Memo',
            styles: { fontStyle: 'bold', halign: 'left' },
          },
        )

        return header
      })(),
    ]

    const Table2Details = () => {
      let voucher_details = []
      if (pdfData && pdfData.voucher_detail && pdfData.voucher_detail.length) {
        pdfData.voucher_detail.map((item) => {
          const row = [
            { content: item.account?.gl_code || '-', styles: { textColor: 'green' } },
            item.account?.gl_account_name || '-',
          ]

          if (showEmployeeColumn) {
            row.push(getEmployeeDisplay(item.employee) || '-')
          }

          if (showPartyColumn) {
            row.push(getPartyDisplay(item.party) || '-')
          }

          row.push(
            { content: item.debit !== 0 ? wtFormatRoundedValue(item.debit, "") : '-', styles: { halign: 'right', overflow: 'hidden' } },
            { content: item.credit !== 0 ? wtFormatRoundedValue(item.credit, "") : '-', styles: { halign: 'right', overflow: 'hidden' } },
            { content: item.narration || '-', styles: { overflow: 'linebreak', valign: 'middle' } },
          )

          voucher_details.push(row)
        })
      }

      return voucher_details
    }
    const gapBetweenTables = bankSpacerRendered
      ? doc.autoTable.previous.finalY + 2
      : Math.max(yPosition, bankSectionBottomY + 2)
    let startYForTable12 = yPosition;
    if ([SOURCE_TYPE_DEFAULT, SOURCE_TYPE_JV_PAYMENT].includes(pdfData.voucher.source_type)) {

      startYForTable12 =
        pdfData.voucher.type !== VOUCHER_TYPE_JOURNAL_VOUCHER
          ? gapBetweenTables
          : yPosition
    }

    let totalDebit = 0
    let totalCredit = 0

    if (pdfData && pdfData.voucher_detail && pdfData.voucher_detail.length) {
      pdfData.voucher_detail.forEach((item) => {
        totalDebit += parseFloat(item.debit) || 0
        totalCredit += parseFloat(item.credit) || 0
      })
    }
    let totalColSpan = 2
    if (showEmployeeColumn) totalColSpan++
    if (showPartyColumn) totalColSpan++
    const isJvPaymentLayout = pdfData?.pdf_meta?.headerLayout === 'jv_payment'
    const isBpvVoucher = pdfData?.voucher?.type === VOUCHER_TYPE_BANK_PAYMENT_VOUCHER
    const tableLeftRightMargin = isJvPaymentLayout ? 8 : 14
    const printableTableWidth = pageWidth - tableLeftRightMargin * 2
    const tableSafetyWidth = isJvPaymentLayout ? 2 : 4
    const effectiveTableWidth = printableTableWidth - tableSafetyWidth
    const glCodeColumnWidth = isJvPaymentLayout ? 22 : 24
    const measureTextWidth = (text, fontSize, fontStyle = 'normal') => {
      const previousFontSize = doc.getFontSize()
      const previousFont = doc.getFont()

      doc.setFont('Helvetica', fontStyle)
      doc.setFontSize(fontSize)
      const width = doc.getTextWidth(`${text || ''}`)
      doc.setFont(previousFont.fontName, previousFont.fontStyle)
      doc.setFontSize(previousFontSize)

      return width
    }
    const variableColumnConfigs = [{ index: 1, weight: 1 }]
    const totalAmountFontSize = isJvPaymentLayout ? 10 : 12
    const bodyAmountValues = (pdfData?.voucher_detail || []).flatMap((item) => [
      item?.debit ? wtFormatRoundedValue(item.debit, "") : '-',
      item?.credit ? wtFormatRoundedValue(item.credit, "") : '-',
    ])
    const totalAmountValues = [
      wtFormatRoundedValue(totalDebit.toFixed(2), "") || '',
      wtFormatRoundedValue(totalCredit.toFixed(2), "") || '',
    ]
    const amountHeaderWidth = Math.max(
      measureTextWidth('Debit', 10, 'bold'),
      measureTextWidth('Credit', 10, 'bold'),
    )
    const widestAmountTextWidth = [...bodyAmountValues, ...totalAmountValues].reduce((maxWidth, value) => {
      const bodyWidth = measureTextWidth(value, 10, 'normal')
      const totalWidth = measureTextWidth(value, totalAmountFontSize, 'bold')
      return Math.max(maxWidth, bodyWidth, totalWidth)
    }, amountHeaderWidth)
    const amountColumnHorizontalPadding = isBpvVoucher ? 6 : isJvPaymentLayout ? 4 : 6
    const debitCreditColumnWidth = Math.max(
      isBpvVoucher ? 18 : isJvPaymentLayout ? 24 : 26,
      Math.ceil(widestAmountTextWidth) + amountColumnHorizontalPadding,
    )
    const tableColumnStyles = {
      0: { cellWidth: glCodeColumnWidth, overflow: 'hidden' },
    }
    if (showEmployeeColumn) {
      variableColumnConfigs.push({ index: 2, weight: isJvPaymentLayout ? 0.9 : 1 })
    }
    if (showPartyColumn) {
      variableColumnConfigs.push({
        index: showEmployeeColumn ? 3 : 2,
        weight: isJvPaymentLayout ? 0.85 : 1,
      })
    }
    const debitColumnIndex = 2 + (showEmployeeColumn ? 1 : 0) + (showPartyColumn ? 1 : 0)
    const creditColumnIndex = debitColumnIndex + 1
    const memoColumnIndex = creditColumnIndex + 1

    const minMemoColumnWidth =
      !showEmployeeColumn && !showPartyColumn
        ? isJvPaymentLayout ? 46 : 50
        : isJvPaymentLayout ? 28 : 36
    const variableWidthBudget =
      effectiveTableWidth -
      glCodeColumnWidth -
      debitCreditColumnWidth -
      debitCreditColumnWidth -
      minMemoColumnWidth
    const totalVariableWeight = variableColumnConfigs.reduce((sum, config) => sum + config.weight, 0)
    const maxAccountColumnWidth = isJvPaymentLayout ? 70 : 76
    const defaultMinVariableColumnWidth = isJvPaymentLayout ? 30 : 32
    const hardMinVariableColumnWidth = isJvPaymentLayout ? 16 : 20
    const minVariableColumnWidth = Math.max(
      hardMinVariableColumnWidth,
      Math.min(
        defaultMinVariableColumnWidth,
        Math.floor(variableWidthBudget / variableColumnConfigs.length),
      ),
    )
    const baseVariableWidth = minVariableColumnWidth * variableColumnConfigs.length
    const remainingVariableWidth = Math.max(0, variableWidthBudget - baseVariableWidth)

    if (variableColumnConfigs.length === 1) {
      tableColumnStyles[1] = {
        cellWidth: Math.min(maxAccountColumnWidth, Math.max(minVariableColumnWidth, variableWidthBudget)),
        overflow: 'linebreak',
      }
    } else {
      variableColumnConfigs.forEach((config) => {
        tableColumnStyles[config.index] = {
          cellWidth:
            minVariableColumnWidth +
            Math.floor((remainingVariableWidth * config.weight) / totalVariableWeight),
          overflow: 'linebreak',
        }
      })
      tableColumnStyles[1].cellWidth = Math.min(maxAccountColumnWidth, tableColumnStyles[1].cellWidth)
    }

    tableColumnStyles[debitColumnIndex] = {
      cellWidth: debitCreditColumnWidth,
      halign: 'right',
      overflow: 'hidden',
    }
    tableColumnStyles[creditColumnIndex] = {
      cellWidth: debitCreditColumnWidth,
      halign: 'right',
      overflow: 'hidden',
    }
    const consumedVariableWidth = variableColumnConfigs.reduce(
      (sum, config) => sum + (tableColumnStyles[config.index]?.cellWidth || 0),
      0,
    )
    tableColumnStyles[memoColumnIndex] = {
      cellWidth:
        effectiveTableWidth -
        glCodeColumnWidth -
        debitCreditColumnWidth -
        debitCreditColumnWidth -
        consumedVariableWidth,
      overflow: 'linebreak',
    }
    doc.autoTable({
      startY: startYForTable12,
      margin: { top: 20, left: tableLeftRightMargin, right: tableLeftRightMargin },
      head: Table2,
      body: Table2Details(),
      foot: [
        [
          {
            content: 'Total',
            colSpan: totalColSpan,
            styles: { halign: 'right', textColor: 'blue', fontSize: totalAmountFontSize, fontStyle: 'bold' },
          },
          {
            content: wtFormatRoundedValue(totalDebit.toFixed(2), "")
              ? `${wtFormatRoundedValue(totalDebit.toFixed(2), "")}`
              : '',
            styles: { textColor: 'blue', fontSize: totalAmountFontSize, fontStyle: 'bold', halign: 'right', overflow: 'hidden' },
          },
          {
            content: wtFormatRoundedValue(totalCredit.toFixed(2), "")
              ? `${wtFormatRoundedValue(totalCredit.toFixed(2), "")}`
              : '',
            styles: { textColor: 'blue', fontSize: totalAmountFontSize, fontStyle: 'bold', halign: 'right', overflow: 'hidden' },
          },

          {
            content: '',
          },
        ],
      ],
      theme: 'plain',
      footStyles: { halign: 'right' },
      tableLineColor: '#3c4b64',
      tableLineWidth: 0.2,
      tableWidth: effectiveTableWidth,
      headStyles: { fillColor: [217, 218, 219] },
      columnStyles: tableColumnStyles,
      styles: {
        lineColor: '#3c4b64',
        lineWidth: 0.2,
        overflow: 'linebreak',
        font: 'helvetica',
        cellPadding: { top: 2, right: 2, bottom: 2, left: 2 },
        valign: 'middle',
      },
      didDrawPage: function (data) { },
    })

    const Table3 = [
      [
        {
          content: 'Amount in words :',
          styles: { halign: 'left', fontStyle: 'bold', fontSize: 9 },
        },
        {
          content: ` ${pdfData.voucher.amount_in_words ? pdfData.voucher.amount_in_words : ''
            } Rupees only.`,
          styles: {
            halign: 'left',
            fontStyle: 'normal',
            textColor: '#0202f7',
            fontSize: 9,
          },
        },
      ],
    ]

    const gapBetweenTable23 = 3
    const startYForTable23 = doc.autoTable.previous.finalY + gapBetweenTable23
    doc.autoTable({
      startY: startYForTable23,
      margin: { top: 20 },
      body: Table3, // Body rows for second table
      theme: 'plain',
      tableLineColor: '#ffffff',
      tableLineWidth: 0,
      tableWidth: 'auto', // Set table width automatically
      styles: {
        lineColor: '#ffffff',
        lineWidth: 0,
        halign: 'center',
        font: 'helvetica',
      },
      columnStyles: {
        0: { columnWidth: 32 },
        1: { columnWidth: 150 },
      },
      didDrawPage: function (data) { },
    })

    doc.setTextColor('#000000')
    doc.setFont('Helvetica', 'bold')
    doc.setFontSize(10)

    const gapBetweenTable = 9
    const startYForTable = doc.autoTable.previous.finalY + gapBetweenTable

    const PreparedBy = 'Prepared By'
    const ApprovedBy = 'Audited By'
    const PostedBy = 'Approved By'


    const PreparedByValue = pdfData.voucher.created_by_id ? pdfData.voucher.created_by.name : ''
    const ApprovedByValue = (pdfData.voucher.approved_by_id && pdfData.voucher.source_type !== SOURCE_TYPE_SALARY_PAYMENT) ? pdfData.voucher.approved_by.name : ''
    const PostedByValue = pdfData.voucher.posted_by_id ? pdfData.voucher.posted_by.name : ''

    // Coordinates for PreparedBy
    const infoX = 16
    const infoY = startYForTable + 9

    // Coordinates for PostedBy
    const rightInfoX = 145
    const rightInfoY = startYForTable + 9

    // Coordinates for ApprovedBy
    const centerInfoX = 80
    const centerInfoY = startYForTable + 9

    const centerText = (text, x, width) => {
      const textWidth =
        (doc.getStringUnitWidth(text) * doc.internal.getFontSize()) / doc.internal.scaleFactor
      const adjustedX = x + (width - textWidth) / 2
      return adjustedX
    }

    doc.setFontSize(9)

    // Display PreparedByValue above the line
    const lineYAbovePreparedBy = infoY - 4
    doc.setTextColor('#0501ff')
    doc.text(PreparedByValue, centerText(PreparedByValue, infoX, 50), lineYAbovePreparedBy)


    // Draw a horizontal line above PreparedBy details
    const lineYAbovePreparedByDetails = infoY - 3
    doc.setLineWidth(0.4)
    doc.line(infoX - 0, lineYAbovePreparedByDetails, infoX + 50, lineYAbovePreparedByDetails)

    doc.setTextColor('#000000')
    doc.text(PreparedBy, centerText(PreparedBy, infoX, 50), infoY)

    // Draw a horizontal line above right side details
    const lineYAboveRightInfoDetails = rightInfoY - 3
    doc.setLineWidth(0.4)
    doc.line(rightInfoX - 0, lineYAboveRightInfoDetails, rightInfoX + 50, lineYAboveRightInfoDetails)

    const lineYAboveRightInfo = rightInfoY - 4
    doc.setTextColor('#0501ff')
    doc.text(PostedByValue, centerText(PostedByValue, rightInfoX, 50), lineYAboveRightInfo)

    doc.setTextColor('#000000')
    doc.text(PostedBy, centerText(PostedBy, rightInfoX, 50), rightInfoY)
    // Display ApprovedByValue above the line
    const lineYAboveCenterInfo = centerInfoY - 4
    doc.setTextColor('#0501ff')
    doc.text(ApprovedByValue, centerText(ApprovedByValue, centerInfoX, 50), lineYAboveCenterInfo)

    // Draw a horizontal line above center side details
    const lineYAboveCenterInfoDetails = centerInfoY - 3
    doc.setLineWidth(0.4)
    doc.line(
      centerInfoX - 0,
      lineYAboveCenterInfoDetails,
      centerInfoX + 50,
      lineYAboveCenterInfoDetails,
    )

    doc.setTextColor('#000000')
    doc.text(ApprovedBy, centerText(ApprovedBy, centerInfoX, 50), centerInfoY)

    ////////DATE TIME////////////

    const gapBetweenNameTime = 12.9
    const startYForDateTime = doc.autoTable.previous.finalY + gapBetweenNameTime

    const CreatedAtValue = wtGetDateTimeFormat(
      pdfData.voucher.created_at ? pdfData.voucher.created_at : '',
    )
    const PostedAtValue = wtGetDateTimeFormat(
      pdfData.voucher.posted_at ? pdfData.voucher.posted_at : '',
    )
    const ApprovedAtValue =
      pdfData.voucher.approved_at && pdfData.voucher.source_type !== SOURCE_TYPE_SALARY_PAYMENT ? wtGetDateTimeFormat(pdfData.voucher.approved_at) : ''

    // Coordinates for CreatedAt
    const infoX1 = 16
    const infoY1 = startYForDateTime + 12.9

    // Coordinates for PostedAt
    const rightInfoX1 = 145
    const rightInfoY1 = startYForDateTime + 12.9

    // Coordinates for ApprovedAt
    const centerInfoX1 = 80
    const centerInfoY1 = startYForDateTime + 12.9

    const centerText1 = (text, x, width) => {
      const textWidth =
        (doc.getStringUnitWidth(text) * doc.internal.getFontSize()) / doc.internal.scaleFactor
      const adjustedX = x + (width - textWidth) / 2
      return adjustedX
    }

    doc.setFontSize(9)

    // Display CreatedAtValue above the line
    const lineYAboveCreatedAt = infoY1 - 4
    doc.setTextColor('#0501ff')
    doc.text(CreatedAtValue, centerText1(CreatedAtValue, infoX1, 50), lineYAboveCreatedAt)

    // Display PostedAtValue above the line
    const lineYAboveRightInfo1 = rightInfoY1 - 4
    doc.setTextColor('#0501ff')
    doc.text(PostedAtValue, centerText1(PostedAtValue, rightInfoX1, 50), lineYAboveRightInfo1)

    // Display ApprovedAtValue above the line
    const lineYAboveCenterInfo1 = centerInfoY1 - 4
    doc.setTextColor('#0501ff')
    doc.text(ApprovedAtValue, centerText1(ApprovedAtValue, centerInfoX1, 50), lineYAboveCenterInfo1)

    const gapAfterThis = 10
    const startYForgapAfterThis = doc.autoTable.previous.finalY + gapAfterThis

    doc.setFontSize(10)
    doc.setTextColor('#000000')
    doc.setFont('Helvetica', 'normal')
    const printReport = 'This voucher is printed through ERP at '
    const currentTime = moment().format('YYYY-MM-DD HH:mm:ss')

    const textWidth1 =
      (doc.getStringUnitWidth(printReport) * doc.internal.getFontSize()) / doc.internal.scaleFactor
    const textWidth2 =
      (doc.getStringUnitWidth(currentTime) * doc.internal.getFontSize()) / doc.internal.scaleFactor
    const totalWidth = textWidth1 + textWidth2

    const startXForPrintReport = (pageWidth - totalWidth) / 2
    const startYForPrintReport = startYForgapAfterThis + 40

    doc.text(printReport, startXForPrintReport, startYForPrintReport)
    doc.setFont('Helvetica', 'bold')
    doc.setTextColor('#0202f7')
    doc.text(currentTime, startXForPrintReport + textWidth1, startYForPrintReport)

    const barcodeY = doc.autoTable.previous.finalY + 50
    const {
      id,
      voucher_no: voucherNo,
      company_voucher_no: companyVoucherNo,
    } = pdfData.voucher || {}

    if (id && voucherNo && companyVoucherNo) {
      const barcodeValue = `${id}-${voucherNo}-${companyVoucherNo}`
      const qrCodeValue = new URL(`/vouchers#view:${id}`, window.location.origin).toString()
      const canvas = document.createElement('canvas')
      const barcodeWidth = 50
      const barcodeHeight = 20
      const qrSize = 16
      const groupWidth = barcodeWidth + qrSize + 4
      const barcodeX = (pageWidth - groupWidth) / 2 - 4
      const qrX = barcodeX + barcodeWidth + 4
      const qrY = barcodeY + 1

      JsBarcode(canvas, barcodeValue, {
        format: 'CODE128',
        displayValue: true,
        fontSize: 32,
      })

      const barcodeDataURL = canvas.toDataURL('image/png')
      const qrCodeDataURL = await QRCode.toDataURL(qrCodeValue, { width: 64, margin: 0 })

      doc.addImage(barcodeDataURL, 'PNG', barcodeX, barcodeY, barcodeWidth, barcodeHeight)
      doc.addImage(qrCodeDataURL, 'PNG', qrX, qrY, qrSize, qrSize)
    }

    if (index < vouchers.length - 1) {
      doc.addPage()
    }
  }

  if (typeof appendPages === 'function') {
    await appendPages(doc)
  }

  const addFooter = (doc) => {
    const pageCount = doc.internal.getNumberOfPages()
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i)
      doc.setTextColor('black')
      doc.setFontSize(9)

      const lineHeight = 286
      const lineWidth = 182
      const lineX = 14
      const lineY = lineHeight
      doc.line(lineX, lineY, lineX + lineWidth, lineY)
      doc.text(`Page ${i} / ${pageCount}`, 190, 290, { align: 'right' })
      doc.text(`Printed By `, 20, 290, { align: 'left' })
      doc.setTextColor('blue')
      doc.text(`${user ? user.name : ''}`, 37, 290, { align: 'left' })
    }
  }
  addFooter(doc)

  // const documentName = `${pdfData.voucher.type_short_name}-${pdfData.voucher.company_voucher_no}-${pdfData.voucher.voucher_no}`;
  // doc.save(`${documentName}.pdf`)

  const pdfBlob = doc.output('blob');
  const pdfUrl = URL.createObjectURL(pdfBlob);
  window.open(pdfUrl, '_blank');
}

export default VoucherPDFBatch
